// ============================================================================
// MÓDULO 7 - UNIDAD 11: EDUCATION & WORD FORMATION
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Temas: Academic subjects, educational places, school supplies, processes
// Word Formation: Prefixes (un-, re-) + Suffixes (-ful, -less)
// Focus: Technical vocabulary for academic contexts
// Integration: Natural phrases combining education + word formation
// ============================================================================

export const M7_UNIT_11 = {
  id: "m7-u11",
  title: "Unit 11: Education & Word Formation",
  topic: "Master academic vocabulary (subjects, places, supplies, processes) and learn to transform words with prefixes and suffixes!",
  
  grammarImage: "/images/grammar/m7-u11-education-word-formation.jpg",
  grammarText: `
    <h3>Education & Word Formation</h3>
    
    <div class="academic-subjects">
      <h4>Academic Subjects (17 Fields)</h4>
      <ul>
        <li><strong>Languages:</strong> English</li>
        <li><strong>Creative:</strong> Art</li>
        <li><strong>Humanities:</strong> History, Philosophy</li>
        <li><strong>Social Sciences:</strong> Geography, Economics, Psychology</li>
        <li><strong>Sciences:</strong> Science (general), Biology, Physics, Chemistry</li>
        <li><strong>STEM:</strong> Math/Maths, Engineering, Architecture</li>
        <li><strong>Professional:</strong> Medicine, Law, Business Studies</li>
      </ul>
    </div>

    <div class="educational-places">
      <h4>Educational Places</h4>
      <ul>
        <li><strong>School</strong> - Escuela (primary/secondary)</li>
        <li><strong>College/University</strong> - Universidad</li>
        <li><strong>Library</strong> - Biblioteca</li>
        <li><strong>Classroom</strong> - Aula</li>
        <li><strong>Laboratory/Lab</strong> - Laboratorio</li>
      </ul>
    </div>

    <div class="school-supplies">
      <h4>School Supplies</h4>
      <ul>
        <li><strong>Desk</strong> - Escritorio</li>
        <li><strong>Pen</strong> - Bolígrafo</li>
        <li><strong>Pencil</strong> - Lápiz</li>
        <li><strong>Eraser (US) / Rubber (UK)</strong> - Borrador</li>
        <li><strong>Ruler</strong> - Regla</li>
        <li><strong>Pencil sharpener</strong> - Sacapuntas</li>
        <li><strong>Exercise book</strong> - Cuaderno</li>
        <li><strong>Textbook</strong> - Libro de texto</li>
      </ul>
    </div>

    <div class="academic-processes">
      <h4>Academic Processes & Achievement</h4>
      <ul>
        <li><strong>Class, Lecture</strong> - Clase, Conferencia</li>
        <li><strong>Homework, Essay, Thesis</strong> - Tarea, Ensayo, Tesis</li>
        <li><strong>Exam, Test</strong> - Examen, Prueba</li>
        <li><strong>Sit an exam</strong> - Presentar un examen</li>
        <li><strong>Pass, Fail</strong> - Aprobar, Reprobar</li>
        <li><strong>Resit</strong> - Repetir examen</li>
        <li><strong>Grade</strong> - Calificación</li>
        <li><strong>Graduate, Degree, Diploma</strong> - Graduarse, Título, Diploma</li>
      </ul>
    </div>

    <div class="word-formation">
      <h4>Word Formation - Prefixes & Suffixes</h4>
      
      <div class="prefixes">
        <h5>PREFIXES (at the beginning):</h5>
        
        <p><strong>UN- = NOT / OPPOSITE</strong></p>
        <ul>
          <li>happy → <strong>UN</strong>happy (not happy)</li>
          <li>likely → <strong>UN</strong>likely (not likely)</li>
          <li>tidy → <strong>UN</strong>tidy (not tidy)</li>
          <li>healthy → <strong>UN</strong>healthy (not healthy)</li>
        </ul>
        
        <p><strong>RE- = AGAIN / BACK</strong></p>
        <ul>
          <li>write → <strong>RE</strong>write (write again)</li>
          <li>take → <strong>RE</strong>take (take again)</li>
          <li>build → <strong>RE</strong>build (build again)</li>
          <li>organize → <strong>RE</strong>organize (organize again)</li>
        </ul>
      </div>
      
      <div class="suffixes">
        <h5>SUFFIXES (at the end):</h5>
        
        <p><strong>-FUL = FULL OF</strong></p>
        <ul>
          <li>success → success<strong>FUL</strong> (full of success)</li>
          <li>hope → hope<strong>FUL</strong> (full of hope)</li>
          <li>wonder → wonder<strong>FUL</strong> (full of wonder)</li>
          <li>rest → rest<strong>FUL</strong> (full of rest = relaxing)</li>
        </ul>
        
        <p><strong>-LESS = WITHOUT</strong></p>
        <ul>
          <li>taste → taste<strong>LESS</strong> (without taste)</li>
          <li>point → point<strong>LESS</strong> (without point)</li>
          <li>rest → rest<strong>LESS</strong> (without rest = anxious)</li>
          <li>hope → hope<strong>LESS</strong> (without hope)</li>
        </ul>
      </div>
    </div>

    <div class="natural-integration">
      <h4>Natural Integration Examples</h4>
      <ul>
        <li>"Jane is <strong>unlikely</strong> to study <strong>history</strong> because she prefers <strong>science</strong>."</li>
        <li>"Tom was <strong>rewriting</strong> his <strong>essay</strong> because his <strong>teacher</strong> gave him a low <strong>grade</strong>."</li>
        <li>"The <strong>school</strong> play was <strong>successful</strong>."</li>
        <li>"The cafeteria food is <strong>tasteless</strong>."</li>
        <li>"Your <strong>desk</strong> is very <strong>untidy</strong>."</li>
        <li>"I'll have to <strong>retake</strong> my <strong>exams</strong>."</li>
        <li>"I am <strong>hopeful</strong> after I <strong>passed</strong> my exams."</li>
      </ul>
    </div>
  `,

  iconType: "education",

  sets: [
    // ========================================================================
    // SET 1: ACADEMIC SUBJECTS 📖
    // ========================================================================
    {
      id: "m7-u11-s1",
      title: "Set 1: Academic Subjects - All Fields 📖",
      context: `**University Course Selection**

Academic advisor: "What subjects interest you? Let me explain our programs..."

Student: "I'm not sure yet - tell me about all the options!"`,
      
      questions: [
        {
          type: "match",
          q: "Match subjects with their fields:",
          pairs: [
            { left: "Biology", right: "Life science (study of living things)" },
            { left: "History", right: "Humanities (study of past events)" },
            { left: "Economics", right: "Social science (study of money/markets)" },
            { left: "Engineering", right: "STEM (applied science/technology)" }
          ],
          successtip: "✓ SUBJECT FIELDS!\nEach subject belongs to a broader academic category! Foundation knowledge! 📚"
        },
        {
          type: "mc",
          q: "Student: 'I love literature and grammar. Which subject?' Advisor: '___'",
          options: [
            "English",
            "Math",
            "Physics"
          ],
          a: "English",
          successtip: "✓ LANGUAGE STUDIES!\nEnglish = study of language, literature, writing, grammar! 📖✍️"
        },
        {
          type: "blank",
          q: "Advisor: 'If you love painting and sculpture, study ___.' (creative field)",
          a: "Art",
          accept: { ignoreCase: true },
          successtip: "✓ CREATIVE ARTS!\nArt = painting, sculpture, drawing, design! Visual creativity! 🎨"
        },
        {
          type: "mc",
          q: "Student: 'I want to study ancient civilizations and wars.' Advisor: '___'",
          options: [
            "History",
            "Geography",
            "Science"
          ],
          a: "History",
          successtip: "✓ PAST EVENTS!\nHistory = study of past events, civilizations, wars, cultures! 📜🏛️"
        },
        {
          type: "order",
          q: "Traduce: Estudio geografía porque me encantan los mapas y países",
          a: "I study geography because I love maps and countries",
          successtip: "✓ EARTH SCIENCE!\nGeography = study of places, maps, countries, landscapes, populations! 🗺️🌍"
        },
        {
          type: "mc",
          q: "Advisor: '___ includes biology, physics, and chemistry.' (general term)",
          options: [
            "Science",
            "Math",
            "Art"
          ],
          a: "Science",
          successtip: "✓ GENERAL SCIENCE!\nScience = broad term covering biology, chemistry, physics, etc! 🔬"
        },
        {
          type: "blank",
          q: "Student: 'I want to study living organisms and ecosystems.' → Study ___",
          a: "Biology",
          accept: { ignoreCase: true },
          successtip: "✓ LIFE SCIENCE!\nBiology = study of living things, plants, animals, cells, ecosystems! 🌱🐾"
        },
        {
          type: "mc",
          q: "Advisor: 'Study ___ if you love motion, energy, and forces.'",
          options: [
            "Physics",
            "Chemistry",
            "Art"
          ],
          a: "Physics",
          successtip: "✓ PHYSICAL SCIENCE!\nPhysics = study of matter, energy, forces, motion! Einstein, Newton! ⚡🔭"
        },
        {
          type: "mc",
          q: "Student: 'I want to learn about chemical reactions and elements.' → Study:",
          options: [
            "Chemistry",
            "Biology",
            "Geography"
          ],
          a: "Chemistry",
          successtip: "✓ CHEMICAL SCIENCE!\nChemistry = study of substances, reactions, elements, compounds! ⚗️🧪"
        },
        {
          type: "blank",
          q: "US: 'Math' = UK: '___' (subject with numbers, algebra, calculus)",
          a: "Maths",
          accept: { ignoreCase: true, alts: ["Mathematics"] },
          successtip: "✓ MATHEMATICS!\nUS: Math. UK: Maths. Both = study of numbers, equations, algebra, calculus! ➕➖✖️➗"
        }
      ]
    },

    // ========================================================================
    // SET 2: EDUCATIONAL PLACES 🏫
    // ========================================================================
    {
      id: "m7-u11-s2",
      title: "Set 2: Educational Institutions & Places 🏫",
      context: `**Campus Tour**

Tour guide: "Welcome to our campus! Let me show you around the different places where you'll study..."

Visitor: "This is impressive! Tell me about each area!"`,
      
      questions: [
        {
          type: "match",
          q: "Match places with their purposes:",
          pairs: [
            { left: "Library", right: "Borrow books and study quietly" },
            { left: "Classroom", right: "Where lessons are taught" },
            { left: "Laboratory", right: "Where science experiments happen" },
            { left: "University", right: "Higher education institution" }
          ],
          successtip: "✓ CAMPUS LOCATIONS!\nEach place has specific academic purpose! Essential vocabulary! 🏫"
        },
        {
          type: "mc",
          q: "Guide: 'Children ages 5-18 attend ___.' (primary and secondary education)",
          options: [
            "school",
            "university",
            "laboratory"
          ],
          a: "school",
          successtip: "✓ PRIMARY/SECONDARY!\nSchool = elementary + middle + high school! Ages 5-18 typically! 🏫👦👧"
        },
        {
          type: "blank",
          q: "Guide: 'For higher education and degrees, you attend ___ or ___.' (two words)",
          a: "college, university",
          accept: { ignoreCase: true, alts: ["university, college", "college / university", "university / college", "university or college", "college or university"] },
          successtip: "✓ HIGHER EDUCATION!\nCollege/University = higher education after school! Degrees and diplomas! 🎓"
        },
        {
          type: "mc",
          q: "Student: 'Where can I borrow books and study quietly?' Guide: 'The ___'",
          options: [
            "library",
            "classroom",
            "cafeteria"
          ],
          a: "library",
          successtip: "✓ LIBRARY!\nLibrary = borrow books, study, research, quiet space! Essential campus place! 📚🤫"
        },
        {
          type: "order",
          q: "Traduce: Las clases se imparten en las aulas",
          a: "Classes are taught in the classrooms",
          successtip: "✓ CLASSROOM!\nClassroom = aula, where lessons are taught! Desks, board, teacher! 🏫👨‍🏫"
        },
        {
          type: "mc",
          q: "Guide: 'Science students do experiments in the ___.'",
          options: [
            "laboratory / lab",
            "library",
            "classroom"
          ],
          a: "laboratory / lab",
          successtip: "✓ LAB!\nLaboratory/Lab = where science experiments happen! Chemistry, biology, physics! 🔬⚗️"
        },
        {
          type: "blank",
          q: "Individual room where one lesson is taught = ___",
          a: "classroom",
          accept: { ignoreCase: true },
          successtip: "✓ CLASSROOM DEFINED!\nClassroom = one room for one class! Multiple classrooms in a school! 🚪📚"
        },
        {
          type: "mc",
          q: "Complete: '___ has multiple classrooms, a library, and a laboratory.'",
          options: [
            "A school / A university",
            "A desk",
            "A pencil"
          ],
          a: "A school / A university",
          successtip: "✓ INSTITUTION!\nSchools and universities contain multiple places inside! Buildings with many rooms! 🏫"
        },
        {
          type: "order",
          q: "Traduce: El laboratorio de química está en el segundo piso",
          a: "The chemistry laboratory is on the second floor",
          successtip: "✓ LAB LOCATION!\nLaboratories often specific: chemistry lab, biology lab, physics lab! 🔬🏢"
        },
        {
          type: "mc",
          q: "Summary: Places go from large to small:",
          options: [
            "University/School → Library/Laboratory → Classroom (large to small)",
            "Classroom → University → Library",
            "All the same size"
          ],
          a: "University/School → Library/Laboratory → Classroom (large to small)",
          successtip: "✓ SIZE HIERARCHY!\nInstitution (large) contains libraries and labs (medium) which contain classrooms (smaller)! 🏫📏"
        }
      ]
    },

    // ========================================================================
    // SET 3: SCHOOL SUPPLIES ✏️
    // ========================================================================
    {
      id: "m7-u11-s3",
      title: "Set 3: School Supplies & Stationery ✏️",
      context: `**Back to School Shopping**

Parent: "Let's get everything you need for school! What's on your list?"

Student: "I need pens, pencils, and more..."`,
      
      questions: [
        {
          type: "match",
          q: "Match school supplies with their uses:",
          pairs: [
            { left: "Pen", right: "Write in ink (permanent)" },
            { left: "Pencil", right: "Write with graphite (erasable)" },
            { left: "Eraser/Rubber", right: "Remove pencil marks" },
            { left: "Ruler", right: "Measure and draw straight lines" }
          ],
          successtip: "✓ ESSENTIAL SUPPLIES!\nEach item has specific purpose! Basic student toolkit! ✏️📏"
        },
        {
          type: "mc",
          q: "Student: 'Where do I sit and write?' Parent: 'At your ___'",
          options: [
            "desk",
            "pen",
            "library"
          ],
          a: "desk",
          successtip: "✓ FURNITURE!\nDesk = table for studying/writing! In classroom and at home! 🪑📚"
        },
        {
          type: "blank",
          q: "Student: 'I need something to write with ink.' Parent: 'Buy a ___' (permanent writing)",
          a: "pen",
          accept: { ignoreCase: true },
          successtip: "✓ PEN!\nPen = writes with ink! Permanent (can't erase easily)! Blue/black/red! 🖊️"
        },
        {
          type: "mc",
          q: "Student: 'I need something erasable for math.' Parent: 'Get a ___'",
          options: [
            "pencil",
            "pen",
            "textbook"
          ],
          a: "pencil",
          successtip: "✓ PENCIL!\nPencil = writes with graphite! Erasable! Perfect for math and sketches! ✏️"
        },
        {
          type: "order",
          q: "Traduce: Necesito un sacapuntas para mi lápiz",
          a: "I need a pencil sharpener for my pencil",
          successtip: "✓ SHARPENER!\nPencil sharpener = makes pencil point sharp! Essential when pencil gets dull! ✏️🔪"
        },
        {
          type: "mc",
          q: "US: 'eraser' = UK: '___' (removes pencil marks)",
          options: [
            "rubber",
            "pen",
            "ruler"
          ],
          a: "rubber",
          successtip: "✓ UK vs US!\nUS: eraser. UK: rubber. Same item - removes pencil! 🇺🇸🇬🇧"
        },
        {
          type: "blank",
          q: "Student: 'I need to draw straight lines and measure.' Parent: 'Buy a ___'",
          a: "ruler",
          accept: { ignoreCase: true },
          successtip: "✓ RULER!\nRuler = measures length and draws straight lines! Usually 30cm or 12 inches! 📏"
        },
        {
          type: "mc",
          q: "Student: 'Where do I do my math homework?' Parent: 'In your ___' (notebook)",
          options: [
            "exercise book",
            "textbook",
            "ruler"
          ],
          a: "exercise book",
          successtip: "✓ EXERCISE BOOK!\nExercise book = notebook for doing exercises/homework! Write in it! 📓✍️"
        },
        {
          type: "order",
          q: "Traduce: El libro de texto tiene toda la información de la materia",
          a: "The textbook has all the subject information",
          successtip: "✓ TEXTBOOK!\nTextbook = book with subject content! Teacher assigns, students READ (don't write in it)! 📖"
        },
        {
          type: "mc",
          q: "Difference: Exercise book vs Textbook:",
          options: [
            "Exercise book = write in it (notebook). Textbook = read from it (information book)",
            "They're the same thing",
            "Textbook = smaller than exercise book"
          ],
          a: "Exercise book = write in it (notebook). Textbook = read from it (information book)",
          successtip: "✓ KEY DIFFERENCE!\nExercise book = YOUR notebook (write/do exercises). Textbook = class book (read/study from)! 📓📖"
        }
      ]
    },

    // ========================================================================
    // SET 4: ACADEMIC ACTIVITIES 📝
    // ========================================================================
    {
      id: "m7-u11-s4",
      title: "Set 4: Academic Activities 📝",
      context: `**Student Life**

New student: "What's a typical day like here?"

Current student: "Let me explain all the activities we do..."`,
      
      questions: [
        {
          type: "match",
          q: "Match academic activities with their descriptions:",
          pairs: [
            { left: "Class", right: "Lesson where teacher instructs" },
            { left: "Homework", right: "Work assigned to do at home" },
            { left: "Exam", right: "Formal test of knowledge" },
            { left: "Essay", right: "Written composition on a topic" }
          ],
          successtip: "✓ ACADEMIC ACTIVITIES!\nThese define student life! What you do daily/weekly! 📚"
        },
        {
          type: "mc",
          q: "Student: 'What do you call the lesson with the teacher?' → '___'",
          options: [
            "A class",
            "Homework",
            "A grade"
          ],
          a: "A class",
          successtip: "✓ CLASS!\nClass = lesson period! Math class, English class, etc! Teacher instructs students! 👨‍🏫📚"
        },
        {
          type: "blank",
          q: "Student: 'University classes are called ___.' (more formal term than 'class')",
          a: "lectures",
          accept: { ignoreCase: true, alts: ["lecture"] },
          successtip: "✓ LECTURE!\nLecture = university-level class! Professor speaks, students take notes! More formal! 🎓👨‍🏫"
        },
        {
          type: "mc",
          q: "Student: 'Work you do at home after school?' → '___'",
          options: [
            "Homework",
            "Classroom",
            "Lecture"
          ],
          a: "Homework",
          successtip: "✓ HOMEWORK!\nHomework = assigned work to do at home! Practice, exercises, reading! 📚🏠"
        },
        {
          type: "order",
          q: "Traduce: Tengo que escribir un ensayo sobre historia",
          a: "I have to write an essay about history",
          successtip: "✓ ESSAY!\nEssay = written composition! Express ideas on a topic! Usually 500-2000 words! ✍️📄"
        },
        {
          type: "mc",
          q: "Student: 'Long research paper for university degree?' → '___'",
          options: [
            "Thesis",
            "Homework",
            "Test"
          ],
          a: "Thesis",
          successtip: "✓ THESIS!\nThesis = major research paper for master's/doctoral degree! Very long, original research! 📚🎓"
        },
        {
          type: "blank",
          q: "Student: 'Formal test of knowledge, usually at end of semester?' → '___'",
          a: "Exam",
          accept: { ignoreCase: true, alts: ["examination"] },
          successtip: "✓ EXAM!\nExam = formal test! End of semester/year! Tests everything learned! 📝😰"
        },
        {
          type: "mc",
          q: "Student: 'Smaller assessment, less formal than exam?' → '___'",
          options: [
            "Test / Quiz",
            "Thesis",
            "Lecture"
          ],
          a: "Test / Quiz",
          successtip: "✓ TEST!\nTest/Quiz = smaller assessment! Less formal than exam! Can be weekly! 📝"
        },
        {
          type: "order",
          q: "Traduce: Necesito repasar para el examen de mañana",
          a: "I need to review for tomorrow's exam",
          successtip: "✓ REVIEW!\nReview (US) = Revise (UK) = study again before exam! Repasar! 📖🔄"
        },
        {
          type: "mc",
          q: "US: 'review' = UK: '___' (study again before exam)",
          options: [
            "revise",
            "homework",
            "class"
          ],
          a: "revise",
          successtip: "✓ US vs UK!\nUS: review. UK: revise. Both = study again, prepare for exam! 📚🇺🇸🇬🇧"
        }
      ]
    },

    // ========================================================================
    // SET 5: ACADEMIC ACHIEVEMENT 🎓
    // ========================================================================
    {
      id: "m7-u11-s5",
      title: "Set 5: Academic Achievement 🎓",
      context: `**Exam Results and Graduation**

Student: "I'm nervous about exam results!"

Friend: "Don't worry - let me explain the whole process from exams to graduation..."`,
      
      questions: [
        {
          type: "match",
          q: "Match terms with their meanings:",
          pairs: [
            { left: "Pass", right: "Succeed in exam (aprobar)" },
            { left: "Fail", right: "Not succeed in exam (reprobar)" },
            { left: "Graduate", right: "Complete degree/program" },
            { left: "Grade", right: "Score/mark on exam (calificación)" }
          ],
          successtip: "✓ RESULTS & ACHIEVEMENT!\nThese define academic success or failure! Critical vocabulary! 📊🎓"
        },
        {
          type: "mc",
          q: "Friend: 'The formal phrase for taking an exam is ___ an exam.'",
          options: [
            "sit",
            "stand",
            "walk"
          ],
          a: "sit",
          successtip: "✓ SIT AN EXAM!\n'Sit an exam' = take/do an exam (formal British)! 'Take an exam' = US version! 📝🪑"
        },
        {
          type: "blank",
          q: "Friend: 'If you get 70% or above, you ___ the exam.' (succeed)",
          a: "pass",
          accept: { ignoreCase: true },
          successtip: "✓ PASS!\nPass = succeed! Get required score! Opposite of fail! ✅😊"
        },
        {
          type: "mc",
          q: "Friend: 'If you get below 60%, you ___ the exam.' (not succeed)",
          options: [
            "fail",
            "pass",
            "graduate"
          ],
          a: "fail",
          successtip: "✓ FAIL!\nFail = not succeed! Below passing score! Have to retake! ❌😞"
        },
        {
          type: "order",
          q: "Traduce: Mi calificación en matemáticas fue 85%",
          a: "My grade in math was 85%",
          successtip: "✓ GRADE!\nGrade = score/mark! Letter (A,B,C) or percentage (85%)! Your result! 📊"
        },
        {
          type: "mc",
          q: "Friend: 'If you fail, you can ___ the exam.' (take again)",
          options: [
            "resit",
            "graduate",
            "lecture"
          ],
          a: "resit",
          successtip: "✓ RESIT!\nResit = retake = take exam again after failing! Second chance! 🔄📝"
        },
        {
          type: "blank",
          q: "Friend: 'When you complete university, you ___.' (finish degree)",
          a: "graduate",
          accept: { ignoreCase: true },
          successtip: "✓ GRADUATE!\nGraduate = complete degree/program! Ceremony with cap and gown! 🎓🎉"
        },
        {
          type: "mc",
          q: "Friend: 'University qualification is called a ___.'",
          options: [
            "degree",
            "homework",
            "classroom"
          ],
          a: "degree",
          successtip: "✓ DEGREE!\nDegree = university qualification! Bachelor's, Master's, Doctorate! 🎓📜"
        },
        {
          type: "order",
          q: "Traduce: Obtuve mi diploma en administración de empresas",
          a: "I got my diploma in business administration",
          successtip: "✓ DIPLOMA!\nDiploma = certificate of qualification! Can be school or university! 📜"
        },
        {
          type: "mc",
          q: "People involved: '___ teaches students, ___ give lectures, ___ learn.'",
          options: [
            "Teacher / Lecturers / Students",
            "Students / Teachers / Lecturers",
            "Lecturers / Students / Teachers"
          ],
          a: "Teacher / Lecturers / Students",
          successtip: "✓ ACADEMIC ROLES!\nTeacher (school), Lecturer (university), Student (learner)! Three main roles! 👨‍🏫👨‍🎓"
        }
      ]
    },

    // ========================================================================
    // SET 6: PREFIX UN- = NOT 🚫
    // ========================================================================
    {
      id: "m7-u11-s6",
      title: "Set 6: Prefix UN- = NOT 🚫",
      context: `**Word Formation: UN- Prefix**

Teacher: "Adding 'UN-' to the beginning of a word makes it mean the OPPOSITE or NOT..."

Student: "Show me examples!"`,
      
      questions: [
        {
          type: "match",
          q: "Match words with their UN- opposites:",
          pairs: [
            { left: "Happy", right: "UNhappy (not happy)" },
            { left: "Likely", right: "UNlikely (not likely)" },
            { left: "Tidy", right: "UNtidy (not tidy)" },
            { left: "Healthy", right: "UNhealthy (not healthy)" }
          ],
          successtip: "✓ UN- PREFIX!\nUN- = NOT or OPPOSITE! Changes meaning to negative! 🚫"
        },
        {
          type: "mc",
          q: "Teacher: 'Happy means alegre. UNhappy means ___.'",
          options: [
            "not happy / sad / triste",
            "very happy",
            "happy again"
          ],
          a: "not happy / sad / triste",
          successtip: "✓ UNHAPPY!\nUN- = NOT! Happy → UNhappy (not happy, sad)! Opposite! 😊→😞"
        },
        {
          type: "blank",
          q: "Teacher: 'Likely means probable. The opposite is ___.' (add UN-)",
          a: "unlikely",
          accept: { ignoreCase: true },
          successtip: "✓ UNLIKELY!\nLikely → UNlikely! Probable → Improbable! UN- makes opposite! 🚫"
        },
        {
          type: "mc",
          q: "Teacher: 'Your desk is messy and disorganized. It's ___.'",
          options: [
            "untidy",
            "tidy",
            "retidy"
          ],
          a: "untidy",
          successtip: "✓ UNTIDY!\nTidy (ordenado) → UNtidy (desordenado)! UN- = NOT tidy! 🚫📚"
        },
        {
          type: "order",
          q: "Traduce: Comer comida chatarra todos los días es poco saludable",
          a: "Eating junk food every day is unhealthy",
          successtip: "✓ UNHEALTHY!\nHealthy → UNhealthy! Not good for health! UN- = NOT! 🍔🚫"
        },
        {
          type: "mc",
          q: "Education context: 'It's ___ that she'll study medicine - she faints at blood!'",
          options: [
            "unlikely (not likely)",
            "likely",
            "relikely"
          ],
          a: "unlikely (not likely)",
          successtip: "✓ UNLIKELY IN CONTEXT!\nUNlikely to study medicine = probably won't! Education + UN- prefix! 🏥🚫"
        },
        {
          type: "blank",
          q: "Complete with UN-: 'Clean up your desk! It's very ___ (not tidy)'",
          a: "untidy",
          accept: { ignoreCase: true },
          successtip: "✓ UNTIDY DESK!\nDesk + untidy = school context! UN- = NOT tidy! 📚🚫"
        },
        {
          type: "mc",
          q: "What does UN- always mean?",
          options: [
            "NOT or OPPOSITE (negativo)",
            "AGAIN (otra vez)",
            "FULL OF"
          ],
          a: "NOT or OPPOSITE (negativo)",
          successtip: "✓ UN- = NOT!\nUN- prefix ALWAYS means NOT or OPPOSITE! Makes negative! 🚫"
        },
        {
          type: "order",
          q: "Complete: 'Fair' (justo) → '___' (injusto) - add UN-",
          a: "Unfair",
          successtip: "✓ UNFAIR!\nFair → UNfair! Just → Unjust! UN- creates opposite! ⚖️🚫"
        },
        {
          type: "mc",
          q: "More UN- examples: unsafe, unusual, uncomfortable all mean:",
          options: [
            "NOT safe, NOT usual, NOT comfortable (all negatives)",
            "Very safe, very usual, very comfortable",
            "Safe again, usual again, comfortable again"
          ],
          a: "NOT safe, NOT usual, NOT comfortable (all negatives)",
          successtip: "✓ UN- PATTERN!\nUN- + adjective = NOT + adjective! Pattern works for many words! 🚫"
        }
      ]
    },

    // ========================================================================
    // SET 7: PREFIX RE- = AGAIN 🔄
    // ========================================================================
    {
      id: "m7-u11-s7",
      title: "Set 7: Prefix RE- = AGAIN 🔄",
      context: `**Word Formation: RE- Prefix**

Teacher: "Adding 'RE-' to the beginning of a verb means doing it AGAIN..."

Student: "That's useful! Show me examples!"`,
      
      questions: [
        {
          type: "match",
          q: "Match verbs with their RE- forms:",
          pairs: [
            { left: "Write", right: "REwrite (write again)" },
            { left: "Take", right: "REtake (take again)" },
            { left: "Build", right: "REbuild (build again)" },
            { left: "Organize", right: "REorganize (organize again)" }
          ],
          successtip: "✓ RE- PREFIX!\nRE- = AGAIN! Do the action another time! 🔄"
        },
        {
          type: "mc",
          q: "Teacher: 'Write means escribir. REwrite means ___.'",
          options: [
            "write again / escribir otra vez",
            "not write",
            "full of writing"
          ],
          a: "write again / escribir otra vez",
          successtip: "✓ REWRITE!\nWrite → REwrite! Do it again! RE- = AGAIN! ✍️🔄"
        },
        {
          type: "blank",
          q: "Teacher: 'If you fail an exam, you ___ it.' (take again - use RE-)",
          a: "retake",
          accept: { ignoreCase: true },
          successtip: "✓ RETAKE!\nTake → REtake! Take exam again after failing! Education context! 📝🔄"
        },
        {
          type: "mc",
          q: "Education context: 'My teacher said my essay was bad, so I'm ___ it.'",
          options: [
            "rewriting (writing again)",
            "unwriting",
            "writeful"
          ],
          a: "rewriting (writing again)",
          successtip: "✓ REWRITING ESSAY!\nREwrite essay = write it again! Teacher feedback → improvement! ✍️🔄"
        },
        {
          type: "order",
          q: "Traduce: Después del terremoto, reconstruyeron la escuela",
          a: "After the earthquake, they rebuilt the school",
          successtip: "✓ REBUILD!\nBuild → REbuild! Build again after destruction! RE- = AGAIN! 🏫🔄"
        },
        {
          type: "mc",
          q: "Teacher: 'My classroom was messy, so I ___ it.' (organize again)",
          options: [
            "reorganized",
            "unorganized",
            "organizeless"
          ],
          a: "reorganized",
          successtip: "✓ REORGANIZE!\nOrganize → REorganize! Arrange again in better way! Classroom context! 📚🔄"
        },
        {
          type: "blank",
          q: "RE- + read = ___. Meaning: read again!",
          a: "reread",
          accept: { ignoreCase: true },
          successtip: "✓ REREAD!\nRead → REread! Read the text again! Study context! 📖🔄"
        },
        {
          type: "mc",
          q: "What does RE- always mean?",
          options: [
            "AGAIN or BACK (otra vez)",
            "NOT or OPPOSITE",
            "FULL OF"
          ],
          a: "AGAIN or BACK (otra vez)",
          successtip: "✓ RE- = AGAIN!\nRE- prefix ALWAYS means AGAIN or BACK! Repeat the action! 🔄"
        },
        {
          type: "order",
          q: "Complete: 'Do' → '___' (do again) - add RE-",
          a: "Redo",
          successtip: "✓ REDO!\nDo → REdo! Do it again! RE- works with many verbs! 🔄"
        },
        {
          type: "mc",
          q: "Academic context: 'I didn't understand, so I'll ___ the chapter.'",
          options: [
            "reread (read again)",
            "unread",
            "readless"
          ],
          a: "reread (read again)",
          successtip: "✓ REREAD FOR UNDERSTANDING!\nREread textbook chapter! Education + RE- prefix! Study again! 📖🔄"
        }
      ]
    },

    // ========================================================================
    // SET 8: SUFFIX -FUL = FULL OF ✨
    // ========================================================================
    {
      id: "m7-u11-s8",
      title: "Set 8: Suffix -FUL = FULL OF ✨",
      context: `**Word Formation: -FUL Suffix**

Teacher: "Adding '-FUL' to the end of a noun makes it mean 'FULL OF' that thing..."

Student: "So it creates positive adjectives?"`,
      
      questions: [
        {
          type: "match",
          q: "Match nouns with their -FUL adjectives:",
          pairs: [
            { left: "Success", right: "SuccessFUL (full of success)" },
            { left: "Hope", right: "HopeFUL (full of hope)" },
            { left: "Wonder", right: "WonderFUL (full of wonder)" },
            { left: "Care", right: "CareFUL (full of care)" }
          ],
          successtip: "✓ -FUL SUFFIX!\n-FUL = FULL OF! Creates adjectives from nouns! Usually positive! ✨"
        },
        {
          type: "mc",
          q: "Teacher: 'Success (éxito) + FUL = ___' (full of success)",
          options: [
            "successful (exitoso)",
            "successless",
            "unsuccess"
          ],
          a: "successful (exitoso)",
          successtip: "✓ SUCCESSFUL!\nSuccess → SuccessFUL! Full of success! Exitoso! ✨🎉"
        },
        {
          type: "blank",
          q: "Teacher: 'Hope (esperanza) + FUL = ___' (full of hope)",
          a: "hopeful",
          accept: { ignoreCase: true },
          successtip: "✓ HOPEFUL!\nHope → HopeFUL! Full of hope! Esperanzado! ✨😊"
        },
        {
          type: "mc",
          q: "Education context: 'The school play was ___!' (full of success)",
          options: [
            "successful",
            "successless",
            "unsuccess"
          ],
          a: "successful",
          successtip: "✓ SUCCESSFUL PLAY!\nSchool play + successful! Education context + -FUL suffix! 🎭✨"
        },
        {
          type: "order",
          q: "Traduce: Estoy esperanzado sobre mi futuro académico",
          a: "I am hopeful about my academic future",
          successtip: "✓ HOPEFUL STUDENT!\nHopeFUL about future! Academic context + -FUL suffix! 🎓✨"
        },
        {
          type: "mc",
          q: "Teacher: 'Wonder (maravilla) + FUL = ___' (full of wonder)",
          options: [
            "wonderful (maravilloso)",
            "wonderless",
            "unwonder"
          ],
          a: "wonderful (maravilloso)",
          successtip: "✓ WONDERFUL!\nWonder → WonderFUL! Full of wonder! Amazing! ✨🌟"
        },
        {
          type: "blank",
          q: "Complete: 'Rest (descanso) + FUL = ___' (full of rest = relaxing)",
          a: "restful",
          accept: { ignoreCase: true },
          successtip: "✓ RESTFUL!\nRest → RestFUL! Full of rest! Relaxing, peaceful! 😌✨"
        },
        {
          type: "mc",
          q: "What does -FUL always mean?",
          options: [
            "FULL OF (lleno de)",
            "WITHOUT",
            "AGAIN"
          ],
          a: "FULL OF (lleno de)",
          successtip: "✓ -FUL = FULL OF!\n-FUL suffix ALWAYS means FULL OF! Creates adjectives! ✨"
        },
        {
          type: "order",
          q: "Complete: 'Use' (uso) + FUL = '___' (full of use = útil)",
          a: "Useful",
          successtip: "✓ USEFUL!\nUse → UseFUL! Full of use! Helpful! ✨👍"
        },
        {
          type: "mc",
          q: "Academic context: 'This textbook is very ___ for studying.' (full of use)",
          options: [
            "useful",
            "useless",
            "unuse"
          ],
          a: "useful",
          successtip: "✓ USEFUL TEXTBOOK!\nTextbook + useful! Education context + -FUL suffix! 📚✨"
        }
      ]
    },

    // ========================================================================
    // SET 9: SUFFIX -LESS = WITHOUT ❌
    // ========================================================================
    {
      id: "m7-u11-s9",
      title: "Set 9: Suffix -LESS = WITHOUT ❌",
      context: `**Word Formation: -LESS Suffix**

Teacher: "Adding '-LESS' to the end of a noun means 'WITHOUT' that thing..."

Student: "So it creates negative adjectives?"`,
      
      questions: [
        {
          type: "match",
          q: "Match nouns with their -LESS adjectives:",
          pairs: [
            { left: "Taste", right: "TasteLESS (without taste)" },
            { left: "Point", right: "PointLESS (without point)" },
            { left: "Rest", right: "RestLESS (without rest)" },
            { left: "Hope", right: "HopeLESS (without hope)" }
          ],
          successtip: "✓ -LESS SUFFIX!\n-LESS = WITHOUT! Creates adjectives from nouns! Usually negative! ❌"
        },
        {
          type: "mc",
          q: "Teacher: 'Taste (sabor) + LESS = ___' (without taste)",
          options: [
            "tasteless (insípido)",
            "tasteful",
            "retaste"
          ],
          a: "tasteless (insípido)",
          successtip: "✓ TASTELESS!\nTaste → TasteLESS! Without taste! Insípido! ❌"
        },
        {
          type: "blank",
          q: "Teacher: 'Point (sentido) + LESS = ___' (without point = sin sentido)",
          a: "pointless",
          accept: { ignoreCase: true },
          successtip: "✓ POINTLESS!\nPoint → PointLESS! Without point! No meaning, useless! ❌"
        },
        {
          type: "mc",
          q: "Education context: 'The cafeteria food is ___.' (without taste)",
          options: [
            "tasteless",
            "tasteful",
            "retaste"
          ],
          a: "tasteless",
          successtip: "✓ TASTELESS FOOD!\nCafeteria + tasteless! Campus context + -LESS suffix! 🍽️❌"
        },
        {
          type: "order",
          q: "Traduce: Estudiar sin un objetivo es inútil (without point)",
          a: "Studying without a goal is pointless",
          successtip: "✓ POINTLESS STUDYING!\nPointLESS = without point! Academic context + -LESS suffix! ❌"
        },
        {
          type: "mc",
          q: "Teacher: 'Rest (descanso) + LESS = ___' (without rest = anxious)",
          options: [
            "restless (inquieto)",
            "restful",
            "rerest"
          ],
          a: "restless (inquieto)",
          successtip: "✓ RESTLESS!\nRest → RestLESS! Without rest! Can't relax, anxious! ❌😰"
        },
        {
          type: "blank",
          q: "Complete: 'Hope (esperanza) + LESS = ___' (without hope)",
          a: "hopeless",
          accept: { ignoreCase: true },
          successtip: "✓ HOPELESS!\nHope → HopeLESS! Without hope! Desesperanzado! ❌😞"
        },
        {
          type: "mc",
          q: "What does -LESS always mean?",
          options: [
            "WITHOUT (sin)",
            "FULL OF",
            "AGAIN"
          ],
          a: "WITHOUT (sin)",
          successtip: "✓ -LESS = WITHOUT!\n-LESS suffix ALWAYS means WITHOUT! Creates negative adjectives! ❌"
        },
        {
          type: "order",
          q: "Complete: 'Care' (cuidado) + LESS = '___' (without care = descuidado)",
          a: "Careless",
          successtip: "✓ CARELESS!\nCare → CareLESS! Without care! Negligent, descuidado! ❌"
        },
        {
          type: "mc",
          q: "Academic context: 'Making ___ mistakes cost me points on the exam.' (without care)",
          options: [
            "careless",
            "careful",
            "recare"
          ],
          a: "careless",
          successtip: "✓ CARELESS MISTAKES!\nExam + careless mistakes! Academic context + -LESS suffix! ❌📝"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE INTEGRATION 🌟
    // ========================================================================
    {
      id: "m7-u11-s10",
      title: "Set 10: Education + Word Formation Together 🌟",
      context: `**FINAL CHALLENGE**

Combining education vocabulary with word formation in natural sentences!`,
      
      questions: [
        {
          type: "mc",
          q: "'Jane is ___ to study history because she prefers science.' (not likely - use UN-)",
          options: [
            "unlikely",
            "likely",
            "relikely"
          ],
          a: "unlikely",
          successtip: "✓ NATURAL INTEGRATION!\nUNlikely + study history + science! Education vocabulary + UN- prefix! 📚🚫"
        },
        {
          type: "blank",
          q: "'Tom was ___ his essay because his teacher gave him a low grade.' (write again)",
          a: "rewriting",
          accept: { ignoreCase: true },
          successtip: "✓ REWRITING ESSAY!\nREwrite + essay + teacher + grade! All education vocab + RE- prefix! ✍️🔄"
        },
        {
          type: "mc",
          q: "'The principal was pleased that the school play was ___.' (full of success)",
          options: [
            "successful",
            "successless",
            "unsuccess"
          ],
          a: "successful",
          successtip: "✓ SUCCESSFUL PLAY!\nSchool + play + successFUL! Education context + -FUL suffix! 🎭✨"
        },
        {
          type: "order",
          q: "Traduce: La comida de la cafetería es insípida (without taste)",
          a: "The cafeteria food is tasteless",
          successtip: "✓ TASTELESS FOOD!\nCafeteria + tasteLESS! Campus life + -LESS suffix! 🍽️❌"
        },
        {
          type: "mc",
          q: "'Please clean up your desk. It's very ___.' (not tidy)",
          options: [
            "untidy",
            "tidy",
            "retidy"
          ],
          a: "untidy",
          successtip: "✓ UNTIDY DESK!\nDesk (school supply) + UNtidy! Education item + UN- prefix! 📚🚫"
        },
        {
          type: "blank",
          q: "'I didn't work hard this year. I'll have to ___ my exams.' (take again)",
          a: "retake",
          accept: { ignoreCase: true },
          successtip: "✓ RETAKE EXAMS!\nREtake + exams! Academic process + RE- prefix! 📝🔄"
        },
        {
          type: "mc",
          q: "'Now that I've passed my exams, I am ___ for the future.' (full of hope)",
          options: [
            "hopeful",
            "hopeless",
            "unhope"
          ],
          a: "hopeful",
          successtip: "✓ HOPEFUL STUDENT!\nPassed exams + hopeFUL! Academic achievement + -FUL suffix! 🎓✨"
        },
        {
          type: "order",
          q: "'The ___ was very informative about biology.' Complete with academic activity.",
          a: "lecture",
          successtip: "✓ BIOLOGY LECTURE!\nLecture + biology! University activity + academic subject! 🎓🔬"
        },
        {
          type: "mc",
          q: "'I need a pencil, eraser, and ruler from the ___.' (supply store context)",
          options: [
            "stationery store / school supplies section",
            "library",
            "laboratory"
          ],
          a: "stationery store / school supplies section",
          successtip: "✓ SCHOOL SUPPLIES!\nPencil + eraser + ruler = all school supplies! Complete vocabulary! ✏️📏"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which shows COMPLETE Unit 11 knowledge?",
          options: [
            "'It's unlikely I'll fail chemistry because I reorganized my study habits. My professor said my last essay was wonderful, so I'm hopeful about graduation!'",
            "'I study school in library with pen. Very good!'",
            "'Education is teacher and student'"
          ],
          a: "'It's unlikely I'll fail chemistry because I reorganized my study habits. My professor said my last essay was wonderful, so I'm hopeful about graduation!'",
          successtip: "🎉 UNIT 11 COMPLETELY MASTERED! 🎉\n\n✅ ACADEMIC SUBJECTS (17 fields!)\n• English, Art, History, Geography ✅\n• Science, Biology, Physics, Chemistry ✅\n• Math/Maths, Medicine, Economics ✅\n• Law, Business, Engineering ✅\n• Architecture, Psychology, Philosophy ✅\n\n✅ EDUCATIONAL PLACES\n• School, College, University ✅\n• Library, Classroom, Laboratory ✅\n• Each with specific purpose! ✅\n\n✅ SCHOOL SUPPLIES\n• Desk, Pen, Pencil, Eraser/Rubber ✅\n• Ruler, Pencil sharpener ✅\n• Exercise book, Textbook ✅\n• UK vs US differences! ✅\n\n✅ ACADEMIC ACTIVITIES\n• Class, Lecture, Homework ✅\n• Essay, Thesis, Exam, Test ✅\n• Review/Revise ✅\n\n✅ ACADEMIC ACHIEVEMENT\n• Sit an exam, Pass, Fail ✅\n• Grade, Resit, Graduate ✅\n• Degree, Diploma, Qualification ✅\n• Teacher, Lecturer, Student ✅\n\n✅ PREFIX UN- = NOT\n• Unlikely, Untidy, Unhealthy ✅\n• Unhappy, Unfair, Unsafe ✅\n• Always means OPPOSITE! ✅\n\n✅ PREFIX RE- = AGAIN\n• Rewrite, Retake, Rebuild ✅\n• Reorganize, Reread, Redo ✅\n• Always means DO AGAIN! ✅\n\n✅ SUFFIX -FUL = FULL OF\n• Successful, Hopeful, Wonderful ✅\n• Restful, Careful, Useful ✅\n• Usually positive adjectives! ✅\n\n✅ SUFFIX -LESS = WITHOUT\n• Tasteless, Pointless, Restless ✅\n• Hopeless, Careless, Useless ✅\n• Usually negative adjectives! ✅\n\n✅ NATURAL INTEGRATION\n• 'Unlikely to study history' ✅\n• 'Rewriting essay for teacher' ✅\n• 'Successful school play' ✅\n• 'Tasteless cafeteria food' ✅\n• 'Untidy desk' ✅\n• 'Retake exams' ✅\n• 'Hopeful for future' ✅\n\n✅ CORRECT IN FINAL SENTENCE:\n• Unlikely (UN- prefix) ✅\n• Fail chemistry (subject + process) ✅\n• Reorganized (RE- prefix) ✅\n• Professor (academic person) ✅\n• Essay (academic work) ✅\n• Wonderful (-FUL suffix) ✅\n• Hopeful (-FUL suffix) ✅\n• Graduation (achievement) ✅\n\n🌟 YOU CAN NOW:\n• Discuss all academic subjects ✅\n• Describe educational places ✅\n• Talk about school supplies ✅\n• Explain academic processes ✅\n• Transform words with prefixes/suffixes ✅\n• Sound professional in academic contexts! ✅\n\nB1-B2 Education & Word Formation = MASTERED! 💪📚\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - MY UNIVERSITY EXPERIENCE (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u11-reading",
    title: "Reading Practice - My University Experience",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Choosing Subjects (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**My University Experience**

**Part 1: Choosing My Subjects**

When I started university, I had to choose my subjects carefully. I'm studying biology and chemistry because I want to study medicine later. It's unlikely I'll take physics - math was never my strength. My friend studies law and economics instead!`
      },
      {
        type: "mc",
        q: "Which subjects is the narrator studying?",
        options: [
          "Biology and chemistry",
          "Law and economics",
          "Physics and math"
        ],
        a: "Biology and chemistry",
        successtip: "✓ ACADEMIC SUBJECTS!\nBiology + chemistry = preparing for medicine! Science subjects! 🔬"
      },
      {
        type: "blank",
        q: "The narrator says it's ___ they'll take physics. (not likely)",
        a: "unlikely",
        accept: { ignoreCase: true },
        successtip: "✓ UN- PREFIX!\nUNlikely = not likely! UN- makes opposite! Subjects + word formation! 🚫"
      },

      // ======================================================================
      // BLOQUE 2: Campus Life (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 2: Campus Life**

Our university has wonderful facilities! The library is huge with thousands of textbooks. We have modern laboratories for science experiments. My desk in my dorm room was untidy last week, so I reorganized everything. The cafeteria food is unfortunately tasteless though!`
      },
      {
        type: "mc",
        q: "Which educational places are mentioned?",
        options: [
          "Library, laboratories, cafeteria",
          "Only classroom",
          "Only school"
        ],
        a: "Library, laboratories, cafeteria",
        successtip: "✓ CAMPUS PLACES!\nLibrary + laboratories + cafeteria = all university locations! 🏫"
      },
      {
        type: "blank",
        q: "The narrator's desk was ___ (not tidy), so they ___ it (organized again).",
        a: "untidy, reorganized",
        accept: { ignoreCase: true, alts: ["untidy / reorganized"] },
        successtip: "✓ TWO PREFIXES!\nUNtidy (UN- = not) + REorganized (RE- = again)! Word formation! 📚🔄"
      },
      {
        type: "mc",
        q: "The cafeteria food is described as:",
        options: [
          "Tasteless (without taste)",
          "Tasteful",
          "Wonderful"
        ],
        a: "Tasteless (without taste)",
        successtip: "✓ -LESS SUFFIX!\nTasteLESS = without taste! -LESS makes negative! 🍽️❌"
      },

      // ======================================================================
      // BLOQUE 3: Studying Hard (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 3: Studying Hard**

I spend hours in the library with my pencil, pens, and ruler. When writing essays, I often rewrite them several times to improve. I use my exercise book for notes and my textbooks for information. Before exams, I review everything carefully. Preparation is very useful!`
      },
      {
        type: "mc",
        q: "Which school supplies are mentioned?",
        options: [
          "Pencil, pens, ruler, exercise book, textbooks",
          "Only desk",
          "Only eraser"
        ],
        a: "Pencil, pens, ruler, exercise book, textbooks",
        successtip: "✓ ALL SUPPLIES!\nComplete list of school supplies! Essential study items! ✏️📚"
      },
      {
        type: "blank",
        q: "The narrator often ___ essays to improve them. (write again)",
        a: "rewrites",
        accept: { ignoreCase: true, alts: ["rewrite"] },
        successtip: "✓ RE- PREFIX!\nREwrite = write again! RE- = again! Academic activity! ✍️🔄"
      },
      {
        type: "mc",
        q: "Before exams, the narrator does what?",
        options: [
          "Reviews everything (studies again)",
          "Forgets everything",
          "Sleeps all day"
        ],
        a: "Reviews everything (studies again)",
        successtip: "✓ REVIEW!\nReview = study again before exam! Academic process! 📖🔄"
      },

      // ======================================================================
      // BLOQUE 4: Exam Stress (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 4: Exam Stress**

Last semester, I sat three difficult exams in one week! I felt restless and couldn't sleep. Fortunately, I passed all of them with good grades. My friend failed one exam and had to resit it. Now I'm hopeful about my future graduation!`
      },
      {
        type: "mc",
        q: "What does 'sit an exam' mean?",
        options: [
          "Take/do an exam",
          "Sit on an exam paper",
          "Skip an exam"
        ],
        a: "Take/do an exam",
        successtip: "✓ SIT AN EXAM!\n'Sit an exam' = formal way to say 'take an exam'! Academic process! 📝"
      },
      {
        type: "blank",
        q: "The narrator felt ___ (without rest/anxious) during exam week.",
        a: "restless",
        accept: { ignoreCase: true },
        successtip: "✓ -LESS SUFFIX!\nRestLESS = without rest, anxious! -LESS = without! 😰❌"
      },
      {
        type: "mc",
        q: "What happened to the narrator's friend?",
        options: [
          "Failed and had to resit (retake) the exam",
          "Passed everything",
          "Didn't take exams"
        ],
        a: "Failed and had to resit (retake) the exam",
        successtip: "✓ FAIL & RESIT!\nFail = not pass. Resit = take exam again! Academic achievement terms! 📝🔄"
      },
      {
        type: "blank",
        q: "Now the narrator is ___ about graduation. (full of hope)",
        a: "hopeful",
        accept: { ignoreCase: true },
        successtip: "✓ -FUL SUFFIX!\nHopeFUL = full of hope! -FUL = full of! Positive feeling! ✨😊"
      },

      // ======================================================================
      // BLOQUE 5: Graduation Day + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 5: Looking Forward**

After four wonderful years, I'll finally graduate! I'll receive my degree in biology. The whole experience was successful - I passed all my classes and wrote my thesis. My lecturers were helpful. I'm thankful for this amazing education!`
      },
      {
        type: "mc",
        q: "What will the narrator receive at graduation?",
        options: [
          "A degree in biology",
          "A pencil",
          "A classroom"
        ],
        a: "A degree in biology",
        successtip: "✓ DEGREE!\nDegree = university qualification! Biology = subject! Achievement! 🎓📜"
      },
      {
        type: "blank",
        q: "The narrator describes the experience as ___. (full of success)",
        a: "successful",
        accept: { ignoreCase: true },
        successtip: "✓ -FUL SUFFIX!\nSuccessFUL = full of success! -FUL makes positive adjective! ✨🎉"
      },
      {
        type: "mc",
        q: "The lecturers were described as:",
        options: [
          "Helpful (full of help)",
          "Helpless (without help)",
          "Unhelpful (not helpful)"
        ],
        a: "Helpful (full of help)",
        successtip: "✓ HELPFUL!\nHelpFUL = full of help! Lecturers = university teachers! 👨‍🏫✨"
      },
      {
        type: "mc",
        q: "Throughout the entire reading, what was covered?",
        options: [
          "All Unit 11 topics: subjects, places, supplies, processes, achievement, and word formation (UN-, RE-, -FUL, -LESS)",
          "Only subjects",
          "Only word formation"
        ],
        a: "All Unit 11 topics: subjects, places, supplies, processes, achievement, and word formation (UN-, RE-, -FUL, -LESS)",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ ACADEMIC SUBJECTS\n• Biology, chemistry, medicine ✅\n• Law, economics, physics, math ✅\n• All used naturally! ✅\n\n✅ EDUCATIONAL PLACES\n• University, library, laboratories ✅\n• Cafeteria, dorm room ✅\n• Campus life described! ✅\n\n✅ SCHOOL SUPPLIES\n• Pencil, pens, ruler ✅\n• Exercise book, textbooks ✅\n• Desk mentioned! ✅\n\n✅ ACADEMIC PROCESSES\n• Essays, exams, thesis ✅\n• Review, sit exams ✅\n• Classes, lectures ✅\n\n✅ ACADEMIC ACHIEVEMENT\n• Pass, fail, grades ✅\n• Resit, graduate, degree ✅\n• Lecturers mentioned! ✅\n\n✅ PREFIX UN- (NOT)\n• Unlikely (not likely) ✅\n• Untidy (not tidy) ✅\n• Used naturally! ✅\n\n✅ PREFIX RE- (AGAIN)\n• Rewrite (write again) ✅\n• Reorganize (organize again) ✅\n• Review (study again) ✅\n• Resit (take exam again) ✅\n\n✅ SUFFIX -FUL (FULL OF)\n• Wonderful (full of wonder) ✅\n• Useful (full of use) ✅\n• Hopeful (full of hope) ✅\n• Successful (full of success) ✅\n• Helpful (full of help) ✅\n\n✅ SUFFIX -LESS (WITHOUT)\n• Tasteless (without taste) ✅\n• Restless (without rest) ✅\n• Negative meanings! ✅\n\n🌟 NATURAL INTEGRATION!\nEducation vocabulary + word formation = complete academic English! 📚✨"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 11 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u11-review",
    title: "⭐ Unit 11 Review: Education & Word Formation",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which subject studies living organisms?",
        options: [
          "Biology",
          "Physics",
          "Law"
        ],
        a: "Biology",
        successtip: "✓ BIOLOGY!\nBiology = study of living things, cells, ecosystems! Life science! 🌱🔬"
      },
      
      {
        type: "blank",
        q: "Where do students borrow books and study quietly? The ___",
        a: "library",
        accept: { ignoreCase: true },
        successtip: "✓ LIBRARY!\nLibrary = place for books, studying, research! Essential campus location! 📚"
      },
      
      {
        type: "mc",
        q: "US: 'eraser' = UK: '___'",
        options: [
          "rubber",
          "pencil",
          "ruler"
        ],
        a: "rubber",
        successtip: "✓ UK vs US!\nUS: eraser. UK: rubber. Both remove pencil marks! 🇺🇸🇬🇧"
      },
      
      {
        type: "order",
        q: "What does 'sit an exam' mean? → '___' (informal version)",
        a: "take an exam",
        successtip: "✓ SIT AN EXAM!\n'Sit an exam' = formal British. 'Take an exam' = common/US! Same meaning! 📝"
      },
      
      {
        type: "mc",
        q: "Prefix UN- means:",
        options: [
          "NOT or OPPOSITE",
          "AGAIN",
          "FULL OF"
        ],
        a: "NOT or OPPOSITE",
        successtip: "✓ UN- = NOT!\nUN- always makes opposite/negative! Happy → UNhappy! 🚫"
      },
      
      {
        type: "blank",
        q: "Write → RE___ (write again)",
        a: "write",
        accept: { ignoreCase: true, alts: ["rewrite"] },
        successtip: "✓ REWRITE!\nRE- = again! Write → REwrite! Do it again! ✍️🔄"
      },
      
      {
        type: "mc",
        q: "Success + FUL = ___",
        options: [
          "Successful (full of success)",
          "Successless",
          "Unsuccess"
        ],
        a: "Successful (full of success)",
        successtip: "✓ -FUL SUFFIX!\n-FUL = full of! Success → SuccessFUL! Positive adjective! ✨"
      },
      
      {
        type: "order",
        q: "Taste + LESS = ___",
        a: "Tasteless",
        successtip: "✓ -LESS SUFFIX!\n-LESS = without! Taste → TasteLESS! Negative adjective! ❌"
      },
      
      {
        type: "mc",
        q: "'I have to ___ my exam because I failed.' (take again)",
        options: [
          "resit / retake",
          "unexam",
          "examful"
        ],
        a: "resit / retake",
        successtip: "✓ RESIT!\nResit = retake = take exam again after failing! RE- = again! 📝🔄"
      },
      
      {
        type: "mc",
        q: "FINAL: Complete Unit 11 mastery includes:",
        options: [
          "17 subjects + places + supplies + processes + achievement + all word formation (UN-, RE-, -FUL, -LESS)",
          "Only subjects",
          "Only word formation"
        ],
        a: "17 subjects + places + supplies + processes + achievement + all word formation (UN-, RE-, -FUL, -LESS)",
        successtip: "🎉 UNIT 11 MASTERED! 🎉\n\n✅ ACADEMIC SUBJECTS (17!)\n• Languages, Humanities, Sciences ✅\n• STEM, Social Sciences, Professional ✅\n• Complete coverage! ✅\n\n✅ EDUCATIONAL PLACES\n• School, University, College ✅\n• Library, Classroom, Laboratory ✅\n\n✅ SCHOOL SUPPLIES\n• Desk, Pen, Pencil, Eraser/Rubber ✅\n• Ruler, Sharpener, Books ✅\n• UK vs US differences! ✅\n\n✅ ACADEMIC PROCESSES\n• Class, Lecture, Homework ✅\n• Essay, Thesis, Exam, Test ✅\n• Review/Revise ✅\n\n✅ ACHIEVEMENT\n• Sit exam, Pass, Fail, Grade ✅\n• Resit, Graduate, Degree ✅\n• Teacher, Lecturer, Student ✅\n\n✅ PREFIX UN- = NOT\n• Unlikely, Untidy, Unhealthy ✅\n• Makes opposites! ✅\n\n✅ PREFIX RE- = AGAIN\n• Rewrite, Retake, Reorganize ✅\n• Repeat actions! ✅\n\n✅ SUFFIX -FUL = FULL OF\n• Successful, Hopeful, Wonderful ✅\n• Positive adjectives! ✅\n\n✅ SUFFIX -LESS = WITHOUT\n• Tasteless, Pointless, Restless ✅\n• Negative adjectives! ✅\n\n🌟 NATURAL INTEGRATION\n• Education + Word Formation ✅\n• Professional academic English! ✅\n\nB1-B2 Education & Word Formation = COMPLETE! 💪📚\n\n¡Excelente! Unit 11 done! 🚀✨"
      }
    ]
  }
};
